/**
 * useUploadRicevuta — F-008 Phase 5 (IMPL-P5-003).
 *
 * Mutation TanStack Query per il commit della ricevuta AdE firmata.
 * Wrappa `operatorClient.commitRicevuta` (POST /api/v1/operator/ricevuta/commit)
 * con invalidazione delle query keys correlate al successo.
 *
 * NB: la mutation è SOLO il commit — la presign + PUT XHR sono gestite dal
 * `PDFDropzone` refactored (props `getUploadUrl` / `confirmUpload`). Il caller
 * tipico (UploadRicevutaModal, F-008 Phase 6) orchestrerà:
 *   1. PDFDropzone iniettato con `getUploadUrl = operatorClient.presignRicevutaUpload`
 *   2. PDFDropzone iniettato con `confirmUpload`: stash di `{ s3Key, sha256, sizeBytes }` in form state
 *   3. submit del form → `useUploadRicevuta.mutate(commitInput)`
 *
 * Pattern di riferimento (F-007):
 *  - `useSegnalaErrore` per la struttura del mutation hook
 *  - `useTakeInCarico` per l'invalidazione multi-key on success
 *
 * Error mapping (cfr. INTRODUCTION.md F-008 Pattern 3):
 *  Il backend ritorna 4 status code distinti — il caller può discriminare via
 *  `mapUploadRicevutaError(err)` che produce uno union type tipizzato. Lo
 *  hook NON tenta retry automatici (ogni errore è semanticamente significativo:
 *  duplicate protocollo, stato cambiato, storage down — tutti richiedono
 *  intervento umano o refresh del DTO pratica).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CommitRicevutaInput } from '@app/shared';
import { operatorClient, isOperatorApiError } from '../lib/operator-client';

/**
 * Union tipizzato degli errori che `useUploadRicevuta` può produrre dopo il
 * mapping di `OperatorApiError` (basato su HTTP status + body code). Il caller
 * fa narrowing su `type` per mostrare il toast / inline error appropriato.
 *
 *  - `duplicate` : 409 — protocollo AdE già usato (BR-2). Il caller deve
 *                  chiedere all'operatore di verificare il numero protocollo.
 *  - `stale`     : 422 — stato pratica cambiato tra apertura modale e commit
 *                  (es. un altro operatore ha rilasciato/preso in carico).
 *                  Il caller deve invalidare la detail e mostrare lo stato
 *                  corrente (`currentStato`) nel messaggio di errore.
 *  - `storage`   : 500 — S3 HEAD/PUT fallito (infra). Retry manuale OK.
 *  - `generic`   : qualunque altro errore (rete, 401, 403, 400 validation).
 */
export type UploadRicevutaError =
  | { type: 'duplicate'; detail: string }
  | { type: 'stale'; currentStato: string }
  | { type: 'storage' }
  | { type: 'generic'; message: string };

/**
 * Mappa un errore generico (tipicamente `OperatorApiError`) sull'union
 * tipizzata `UploadRicevutaError`. Esportata per i caller / test.
 */
export function mapUploadRicevutaError(err: unknown): UploadRicevutaError {
  if (!isOperatorApiError(err)) {
    const msg = err instanceof Error ? err.message : 'Errore sconosciuto';
    return { type: 'generic', message: msg };
  }

  const body = (err.body ?? {}) as {
    code?: string;
    detail?: string;
    currentStato?: string;
  };

  if (err.status === 409 && body.code === 'OPERATOR_PROTOCOLLO_ADE_DUPLICATE') {
    return { type: 'duplicate', detail: body.detail ?? '' };
  }
  if (err.status === 422 && body.code === 'OPERATOR_RICEVUTA_STATE_ERROR') {
    return { type: 'stale', currentStato: body.currentStato ?? '' };
  }
  if (err.status === 500 && body.code === 'OPERATOR_RICEVUTA_STORAGE_ERROR') {
    return { type: 'storage' };
  }
  return { type: 'generic', message: err.message || `HTTP ${err.status}` };
}

/**
 * Hook mutation per il commit della ricevuta. Invalida tre query keys al
 * successo (cfr. PHASE_5_PLAN.md):
 *  - `['operator', 'queue']`             — la pratica esce dalla queue (COMPLETATA)
 *  - `['operator', 'detail', praticaId]` — il DTO operator detail riflette lo stato
 *  - `['pratiche-cliente', 'list']`      — il cliente vede la pratica completata
 *
 * @param praticaId — ID della pratica per la query key di detail (chiusura).
 *                    Il `commitRicevuta` API call legge `praticaId` dall'input
 *                    della mutation, non da questo parametro.
 */
export function useUploadRicevuta(praticaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CommitRicevutaInput) =>
      operatorClient.commitRicevuta(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'queue'] });
      qc.invalidateQueries({ queryKey: ['operator', 'detail', praticaId] });
      qc.invalidateQueries({ queryKey: ['pratiche-cliente', 'list'] });
    },
  });
}
